import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { getEmbedTargetOrigin } from '../utils/embedOrigin';
import { 
  Save, Plus, Minus, RotateCw, Trash2, Grid3x3,
  Theater, Users, Rows3, LayoutGrid, CircleDot, Bus,
  Check, X, ChevronDown, ChevronUp, Eye, Sparkles
} from 'lucide-react';

const BACKEND_URL = ((typeof window !== 'undefined' && window.location.hostname.includes('seatpilot.co.uk')) ? 'https://seatpilot.co.uk' : (process.env.REACT_APP_BACKEND_URL || 'https://seatpilot.co.uk'));
const API = `${BACKEND_URL}/api`;

// Tool categories for quick creation
const QUICK_TOOLS = {
  theater: { icon: Theater, label: 'Theater Rows', color: '#e2e8f0' },
  banquet: { icon: CircleDot, label: 'Banquet Tables', color: '#ddd6fe' },
  bus: { icon: Bus, label: 'Bus Layout', color: '#60a5fa' },
  custom: { icon: LayoutGrid, label: 'Custom', color: '#fef3c7' }
};

export default function EmbeddableBuilder() {
  const [searchParams] = useSearchParams();
  const canvasRef = useRef(null);
  
  // Get params from URL (passed by SDK)
  const apiKey = searchParams.get('api_key');
  const organiserId = searchParams.get('organiser_id');
  const organiserName = searchParams.get('organiser_name') || '';
  const layoutId = searchParams.get('layout_id');
  const eventId = searchParams.get('event_id');
  const eventName = searchParams.get('event_name') || '';
  const theme = searchParams.get('theme') || 'light';
  const mode = searchParams.get('mode') || 'auto'; // 'quick' | 'full' | 'auto'

  // ──────── Hooks (always called, in stable order) ────────
  const [currentStep, setCurrentStep] = useState('type'); // type, config, preview
  const [layoutType, setLayoutType] = useState(null);
  const [layoutName, setLayoutName] = useState('');
  const [venueName, setVenueName] = useState('');

  // Theater config
  const [rows, setRows] = useState(5);
  const [seatsPerRow, setSeatsPerRow] = useState(10);

  // Banquet config
  const [tables, setTables] = useState(8);
  const [seatsPerTable, setSeatsPerTable] = useState(8);

  // Bus config
  const [busRows, setBusRows] = useState(10);
  const [busSeatsPerRow, setBusSeatsPerRow] = useState(4);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [savedLayout, setSavedLayout] = useState(null);
  const [previewSeats, setPreviewSeats] = useState([]);

  // Should we redirect to the full canvas builder?
  const shouldRedirectToFullBuilder = !!(layoutId || mode === 'full');


  
  // Listen for commands from parent window
  useEffect(() => {
    const handleMessage = (event) => {
      const { type, ...data } = event.data;
      
      switch(type) {
        case 'save_layout':
          handleSave();
          break;
        case 'publish_layout':
          handlePublish();
          break;
        case 'reset_layout':
          resetBuilder();
          break;
        case 'load_layout':
          if (data.layout_id) {
            loadExistingLayout(data.layout_id);
          }
          break;
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);
  
  // Notify parent when ready
  useEffect(() => {
    window.parent.postMessage({ type: 'builder_ready', status: 'ready' }, getEmbedTargetOrigin());
  }, []);
  
  // Notify loading state changes
  useEffect(() => {
    window.parent.postMessage({ type: 'builder_loading', loading }, getEmbedTargetOrigin());
  }, [loading]);
  
  // Load existing layout if layoutId provided
  useEffect(() => {
    if (layoutId && apiKey) {
      loadExistingLayout(layoutId);
    }
  }, [layoutId, apiKey]);

  // Perform redirect for full builder mode (after all hooks declared)
  useEffect(() => {
    if (!shouldRedirectToFullBuilder) return;
    const params = new URLSearchParams({
      embed: 'true',
      api_key: apiKey || '',
      organiser_id: organiserId || '',
      ...(organiserName && { organiser_name: organiserName }),
      ...(eventId && { event_id: eventId }),
      ...(eventName && { event_name: eventName }),
      theme: theme
    });
    const builderUrl = layoutId 
      ? `/builder/${layoutId}?${params.toString()}`
      : `/builder/new?${params.toString()}`;
    window.location.replace(builderUrl);
  }, [shouldRedirectToFullBuilder, apiKey, organiserId, organiserName, eventId, eventName, theme, layoutId]);
  
  // Render redirect spinner once hooks are all in place
  if (shouldRedirectToFullBuilder) {
    return <div className="flex items-center justify-center h-screen"><div className="animate-spin w-8 h-8 border-4 border-violet-600 border-t-transparent rounded-full"></div></div>;
  }
  
  const loadExistingLayout = async (id) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/external/layouts/${id}`, {
        headers: { 'X-API-Key': apiKey }
      });
      
      const data = response.data;
      const layout = data.layout;
      const enriched = {
        ...layout,
        layout_id: data.info?.layout_id || layout.id,
        seat_count: data.seat_stats?.total || 0,
        categories: data.layout?.categories,
        is_published: data.info?.is_published || layout.is_published,
        widget_url: data.urls?.widget_url,
        builder_url: data.urls?.builder_url,
        seat_stats: data.seat_stats
      };
      
      setSavedLayout(enriched);
      setLayoutName(layout.name || data.info?.name || '');
      setVenueName(layout.venue_name || data.info?.venue_name || '');
      setCurrentStep('preview');
      
      // Generate preview from canvas objects
      generatePreviewFromCanvas(layout.canvas_objects || []);
      
      // Notify parent that layout is loaded
      window.parent.postMessage({ type: 'layout_loaded', layout: enriched }, getEmbedTargetOrigin());
    } catch (err) {
      setError('Failed to load layout');
      notifyError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const generatePreviewFromCanvas = (canvasObjects) => {
    const seats = [];
    for (const obj of canvasObjects) {
      if (obj.seats) {
        seats.push(...obj.seats.map(s => ({
          ...s,
          parentType: obj.type,
          parentLabel: obj.label
        })));
      }
    }
    setPreviewSeats(seats);
  };
  
  const generatePreview = () => {
    const seats = [];
    
    if (layoutType === 'theater') {
      for (let r = 0; r < rows; r++) {
        const rowLabel = String.fromCharCode(65 + r);
        for (let s = 1; s <= seatsPerRow; s++) {
          seats.push({
            row: rowLabel,
            seat: s,
            label: `${rowLabel}-${s}`
          });
        }
      }
    } else if (layoutType === 'banquet') {
      for (let t = 1; t <= tables; t++) {
        for (let s = 1; s <= seatsPerTable; s++) {
          seats.push({
            table: t,
            seat: s,
            label: `T${t}-${s}`
          });
        }
      }
    } else if (layoutType === 'bus') {
      for (let r = 0; r < busRows; r++) {
        for (let s = 0; s < busSeatsPerRow; s++) {
          const seatLabel = `${r + 1}${String.fromCharCode(65 + s)}`;
          seats.push({
            row: r + 1,
            seat: s + 1,
            label: seatLabel,
            side: s < 2 ? 'left' : 'right'
          });
        }
      }
    }
    
    setPreviewSeats(seats);
    setCurrentStep('preview');
  };
  
  const handleSave = async () => {
    if (!layoutName.trim()) {
      setError('Please enter a layout name');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const payload = {
        name: layoutName,
        venue_name: venueName,
        organiser_id: organiserId,
        organiser_name: organiserName,
        event_id: eventId,
        event_name: eventName,
        is_published: false
      };
      
      // Add layout type specific config
      if (layoutType === 'theater') {
        payload.quick_create = {
          type: 'theater',
          rows: rows,
          seats_per_row: seatsPerRow
        };
      } else if (layoutType === 'banquet') {
        payload.quick_create = {
          type: 'banquet',
          tables: tables,
          seats_per_table: seatsPerTable
        };
      } else if (layoutType === 'bus') {
        payload.quick_create = {
          type: 'bus',
          bus_config: {
            rows: busRows,
            seats_per_row: busSeatsPerRow,
            aisle_after_seat: 2,
            driver_area: true,
            toilet_at_back: false
          }
        };
      }
      
      let response;
      if (savedLayout?.id) {
        // Update existing
        response = await axios.put(`${API}/external/layouts/${savedLayout.id}`, payload, {
          headers: { 'X-API-Key': apiKey }
        });
      } else {
        // Create new
        response = await axios.post(`${API}/external/layouts`, payload, {
          headers: { 'X-API-Key': apiKey }
        });
      }
      
      const result = response.data;
      
      // Fetch full layout details (same as GET /external/layouts/{id}) for rich response
      let fullLayout = result;
      const layoutIdForFetch = result.layout_id || result.id;
      if (layoutIdForFetch) {
        try {
          const [detailRes, seatsRes] = await Promise.all([
            axios.get(`${API}/external/layouts/${layoutIdForFetch}`, { headers: { 'X-API-Key': apiKey } }),
            axios.get(`${API}/external/layouts/${layoutIdForFetch}/seats`, { headers: { 'X-API-Key': apiKey } })
          ]);
          const detail = detailRes.data;
          const seatsData = seatsRes.data;
          fullLayout = {
            ...result,
            ...detail.layout,
            layout_id: layoutIdForFetch,
            seat_count: detail.seat_stats?.total || seatsData.total_seats || 0,
            categories: seatsData.categories || [],
            seat_stats: detail.seat_stats,
            widget_url: detail.urls?.widget_url,
            seats: seatsData.categories || [],
            total_seats: seatsData.total_seats,
            has_pricing: seatsData.has_pricing,
            currency: seatsData.currency
          };
        } catch {}
      }
      
      setSavedLayout(fullLayout);
      
      // Notify parent with full layout details
      window.parent.postMessage({
        type: 'layout_saved',
        layout: fullLayout
      }, '*');
      
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to save layout';
      setError(errorMsg);
      notifyError(errorMsg);
    } finally {
      setLoading(false);
    }
  };
  
  const handlePublish = async () => {
    if (!savedLayout?.layout_id) {
      // Save first if not saved
      await handleSave();
    }
    
    const layoutIdToPublish = savedLayout?.layout_id || savedLayout?.id;
    if (!layoutIdToPublish) {
      setError('Please save the layout first');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.patch(
        `${API}/external/layouts/${layoutIdToPublish}/publish?is_published=true`,
        {},
        { headers: { 'X-API-Key': apiKey } }
      );
      
      setSavedLayout(prev => ({ ...prev, is_published: true }));
      
      // Notify parent
      window.parent.postMessage({
        type: 'layout_published',
        layout: { ...savedLayout, is_published: true, ...response.data }
      }, '*');
      
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to publish layout';
      setError(errorMsg);
      notifyError(errorMsg);
    } finally {
      setLoading(false);
    }
  };
  
  const notifyError = (message) => {
    window.parent.postMessage({
      type: 'error',
      message: message
    }, '*');
  };
  
  const resetBuilder = () => {
    setCurrentStep('type');
    setLayoutType(null);
    setLayoutName('');
    setVenueName('');
    setRows(5);
    setSeatsPerRow(10);
    setTables(8);
    setSeatsPerTable(8);
    setBusRows(10);
    setBusSeatsPerRow(4);
    setPreviewSeats([]);
    setSavedLayout(null);
    setError(null);
  };
  
  const totalSeats = layoutType === 'theater' ? rows * seatsPerRow 
    : layoutType === 'banquet' ? tables * seatsPerTable 
    : layoutType === 'bus' ? busRows * busSeatsPerRow 
    : 0;
  
  // Validation check
  if (!apiKey || !organiserId) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="text-center p-8">
          <X className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            Configuration Error
          </h2>
          <p className={`mt-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            Missing required parameters: api_key and organiser_id
          </p>
        </div>
      </div>
    );
  }
  
  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      {/* Header */}
      <div className={`border-b ${theme === 'dark' ? 'border-gray-800 bg-gray-900' : 'border-gray-200 bg-white'} px-4 py-3`}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold">
              {savedLayout ? 'Edit Layout' : 'Create Seating Layout'}
            </h1>
            {eventName && (
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                For: {eventName}
              </p>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {savedLayout && (
              <span className={`text-xs px-2 py-1 rounded ${
                savedLayout.is_published 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-amber-100 text-amber-800'
              }`}>
                {savedLayout.is_published ? 'Published' : 'Draft'}
              </span>
            )}
            
            <Button
              variant="outline"
              size="sm"
              onClick={resetBuilder}
              disabled={loading}
            >
              Reset
            </Button>
            
            {currentStep === 'preview' && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSave}
                  disabled={loading}
                  data-testid="embed-builder-save-now"
                  title="Save changes now. Full canvas auto-saves every 5 seconds."
                >
                  <Save className="w-4 h-4 mr-1" />
                  {loading ? 'Saving…' : 'Save Now'}
                </Button>
                
                <Button
                  size="sm"
                  onClick={handlePublish}
                  disabled={loading || !layoutName.trim()}
                  className="bg-violet-600 hover:bg-violet-700"
                  data-testid="embed-builder-publish"
                >
                  <Sparkles className="w-4 h-4 mr-1" />
                  Publish
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* Error Display */}
      {error && (
        <div className="mx-4 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
          {error}
        </div>
      )}
      
      {/* Step 1: Choose Layout Type */}
      {currentStep === 'type' && (
        <div className="p-6 max-w-3xl mx-auto">
          <h2 className="text-xl font-semibold mb-6 text-center">Choose Layout Type</h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(QUICK_TOOLS).map(([type, config]) => (
              <button
                key={type}
                onClick={() => { setLayoutType(type); setCurrentStep('config'); }}
                className={`p-6 rounded-xl border-2 transition-all hover:shadow-lg ${
                  theme === 'dark' 
                    ? 'border-gray-700 hover:border-violet-500 bg-gray-800' 
                    : 'border-gray-200 hover:border-violet-500 bg-white'
                }`}
              >
                <config.icon className="w-10 h-10 mx-auto mb-3" style={{ color: config.color }} />
                <p className="font-medium">{config.label}</p>
              </button>
            ))}
          </div>
        </div>
      )}
      
      {/* Step 2: Configure Layout */}
      {currentStep === 'config' && (
        <div className="p-6 max-w-xl mx-auto">
          <button 
            onClick={() => setCurrentStep('type')}
            className={`mb-4 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} hover:underline`}
          >
            ← Back to layout types
          </button>
          
          <h2 className="text-xl font-semibold mb-6">Configure Your Layout</h2>
          
          <div className="space-y-6">
            {/* Layout Name */}
            <div>
              <Label className="mb-2 block">Layout Name *</Label>
              <Input
                value={layoutName}
                onChange={(e) => setLayoutName(e.target.value)}
                placeholder="e.g., Main Hall - Concert Setup"
                className={theme === 'dark' ? 'bg-gray-800 border-gray-700' : ''}
              />
            </div>
            
            {/* Venue Name */}
            <div>
              <Label className="mb-2 block">Venue Name (optional)</Label>
              <Input
                value={venueName}
                onChange={(e) => setVenueName(e.target.value)}
                placeholder="e.g., City Convention Center"
                className={theme === 'dark' ? 'bg-gray-800 border-gray-700' : ''}
              />
            </div>
            
            {/* Theater Config */}
            {layoutType === 'theater' && (
              <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-100'}`}>
                <h3 className="font-medium mb-4 flex items-center gap-2">
                  <Theater className="w-5 h-5" /> Theater Configuration
                </h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="mb-2 block text-sm">Number of Rows</Label>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => setRows(Math.max(1, rows - 1))}>
                        <Minus className="w-4 h-4" />
                      </Button>
                      <Input
                        type="number"
                        value={rows}
                        onChange={(e) => setRows(Math.max(1, parseInt(e.target.value) || 1))}
                        className={`w-16 text-center ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : ''}`}
                      />
                      <Button variant="outline" size="sm" onClick={() => setRows(rows + 1)}>
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div>
                    <Label className="mb-2 block text-sm">Seats per Row</Label>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => setSeatsPerRow(Math.max(1, seatsPerRow - 1))}>
                        <Minus className="w-4 h-4" />
                      </Button>
                      <Input
                        type="number"
                        value={seatsPerRow}
                        onChange={(e) => setSeatsPerRow(Math.max(1, parseInt(e.target.value) || 1))}
                        className={`w-16 text-center ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : ''}`}
                      />
                      <Button variant="outline" size="sm" onClick={() => setSeatsPerRow(seatsPerRow + 1)}>
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Banquet Config */}
            {layoutType === 'banquet' && (
              <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-100'}`}>
                <h3 className="font-medium mb-4 flex items-center gap-2">
                  <CircleDot className="w-5 h-5" /> Banquet Configuration
                </h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="mb-2 block text-sm">Number of Tables</Label>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => setTables(Math.max(1, tables - 1))}>
                        <Minus className="w-4 h-4" />
                      </Button>
                      <Input
                        type="number"
                        value={tables}
                        onChange={(e) => setTables(Math.max(1, parseInt(e.target.value) || 1))}
                        className={`w-16 text-center ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : ''}`}
                      />
                      <Button variant="outline" size="sm" onClick={() => setTables(tables + 1)}>
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div>
                    <Label className="mb-2 block text-sm">Seats per Table</Label>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => setSeatsPerTable(Math.max(2, seatsPerTable - 1))}>
                        <Minus className="w-4 h-4" />
                      </Button>
                      <Input
                        type="number"
                        value={seatsPerTable}
                        onChange={(e) => setSeatsPerTable(Math.max(2, parseInt(e.target.value) || 2))}
                        className={`w-16 text-center ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : ''}`}
                      />
                      <Button variant="outline" size="sm" onClick={() => setSeatsPerTable(seatsPerTable + 1)}>
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Bus Config */}
            {layoutType === 'bus' && (
              <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-100'}`}>
                <h3 className="font-medium mb-4 flex items-center gap-2">
                  <Bus className="w-5 h-5" /> Bus Configuration
                </h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="mb-2 block text-sm">Number of Rows</Label>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => setBusRows(Math.max(1, busRows - 1))}>
                        <Minus className="w-4 h-4" />
                      </Button>
                      <Input
                        type="number"
                        value={busRows}
                        onChange={(e) => setBusRows(Math.max(1, parseInt(e.target.value) || 1))}
                        className={`w-16 text-center ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : ''}`}
                      />
                      <Button variant="outline" size="sm" onClick={() => setBusRows(busRows + 1)}>
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div>
                    <Label className="mb-2 block text-sm">Seats per Row</Label>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => setBusSeatsPerRow(Math.max(2, busSeatsPerRow - 1))}>
                        <Minus className="w-4 h-4" />
                      </Button>
                      <Input
                        type="number"
                        value={busSeatsPerRow}
                        onChange={(e) => setBusSeatsPerRow(Math.max(2, parseInt(e.target.value) || 2))}
                        className={`w-16 text-center ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : ''}`}
                      />
                      <Button variant="outline" size="sm" onClick={() => setBusSeatsPerRow(busSeatsPerRow + 1)}>
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Custom - redirect to full builder */}
            {layoutType === 'custom' && (
              <div className={`p-6 rounded-lg text-center ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-100'}`}>
                <LayoutGrid className="w-12 h-12 mx-auto mb-4 text-amber-500" />
                <h3 className="font-medium mb-2">Advanced Layout Builder</h3>
                <p className={`text-sm mb-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  For custom layouts with mixed seating types, use our full-featured builder.
                </p>
                <Button
                  onClick={() => window.open(`${BACKEND_URL}/builder/new?organiser_id=${organiserId}`, '_blank')}
                  className="bg-violet-600 hover:bg-violet-700"
                >
                  Open Advanced Builder
                </Button>
              </div>
            )}
            
            {/* Total Seats Summary */}
            {layoutType !== 'custom' && (
              <div className={`p-4 rounded-lg border ${theme === 'dark' ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-white'}`}>
                <div className="flex items-center justify-between">
                  <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Total Capacity</span>
                  <span className="text-2xl font-bold text-violet-600">{totalSeats} seats</span>
                </div>
              </div>
            )}
            
            {/* Preview Button */}
            {layoutType !== 'custom' && (
              <Button
                onClick={generatePreview}
                disabled={!layoutName.trim()}
                className="w-full bg-violet-600 hover:bg-violet-700"
                size="lg"
              >
                <Eye className="w-5 h-5 mr-2" />
                Preview Layout
              </Button>
            )}
          </div>
        </div>
      )}
      
      {/* Step 3: Preview */}
      {currentStep === 'preview' && (
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <button 
                onClick={() => setCurrentStep('config')}
                className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} hover:underline`}
              >
                ← Back to configuration
              </button>
              <h2 className="text-xl font-semibold mt-2">{layoutName || 'Preview'}</h2>
              {venueName && (
                <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                  {venueName}
                </p>
              )}
            </div>
            
            <div className={`text-right ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              <p className="text-sm">Total Capacity</p>
              <p className="text-2xl font-bold text-violet-600">{previewSeats.length}</p>
            </div>
          </div>
          
          {/* Seat Preview */}
          <div className={`rounded-xl border overflow-auto ${theme === 'dark' ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`} style={{ maxHeight: '400px' }}>
            <div className="p-6 min-w-fit">
              {layoutType === 'theater' && (
                <div className="space-y-2">
                  {/* Screen indicator */}
                  <div className={`mx-auto w-3/4 h-8 rounded flex items-center justify-center text-sm font-medium mb-6 ${theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-300 text-gray-700'}`}>
                    SCREEN
                  </div>
                  
                  {Array.from({ length: rows }).map((_, rowIdx) => {
                    const rowLabel = String.fromCharCode(65 + rowIdx);
                    return (
                      <div key={rowIdx} className="flex items-center gap-2">
                        <span className={`w-6 text-xs font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                          {rowLabel}
                        </span>
                        <div className="flex gap-1">
                          {Array.from({ length: seatsPerRow }).map((_, seatIdx) => (
                            <div
                              key={seatIdx}
                              className={`w-6 h-6 rounded text-[10px] flex items-center justify-center ${theme === 'dark' ? 'bg-gray-600' : 'bg-gray-200'}`}
                              title={`${rowLabel}-${seatIdx + 1}`}
                            >
                              {seatIdx + 1}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              
              {layoutType === 'banquet' && (
                <div className="grid grid-cols-4 gap-6">
                  {Array.from({ length: tables }).map((_, tableIdx) => (
                    <div key={tableIdx} className="flex flex-col items-center">
                      <div className={`w-20 h-20 rounded-full flex items-center justify-center font-medium ${theme === 'dark' ? 'bg-violet-900/50 border-2 border-violet-700' : 'bg-violet-100 border-2 border-violet-300'}`}>
                        T{tableIdx + 1}
                      </div>
                      <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                        {seatsPerTable} seats
                      </p>
                    </div>
                  ))}
                </div>
              )}
              
              {layoutType === 'bus' && (
                <div className={`inline-block p-4 rounded-xl ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  {/* Driver area */}
                  <div className={`mb-4 p-2 rounded text-center text-sm ${theme === 'dark' ? 'bg-gray-600' : 'bg-gray-300'}`}>
                    DRIVER
                  </div>
                  
                  <div className="space-y-1">
                    {Array.from({ length: busRows }).map((_, rowIdx) => (
                      <div key={rowIdx} className="flex items-center gap-4">
                        {/* Left side */}
                        <div className="flex gap-1">
                          {Array.from({ length: Math.floor(busSeatsPerRow / 2) }).map((_, seatIdx) => (
                            <div
                              key={seatIdx}
                              className="w-8 h-7 rounded bg-blue-400 text-white text-[10px] flex items-center justify-center font-medium"
                            >
                              {rowIdx + 1}{String.fromCharCode(65 + seatIdx)}
                            </div>
                          ))}
                        </div>
                        
                        {/* Aisle */}
                        <div className={`w-6 h-7 ${theme === 'dark' ? 'bg-gray-600' : 'bg-gray-200'} rounded`}></div>
                        
                        {/* Right side */}
                        <div className="flex gap-1">
                          {Array.from({ length: Math.ceil(busSeatsPerRow / 2) }).map((_, seatIdx) => (
                            <div
                              key={seatIdx}
                              className="w-8 h-7 rounded bg-blue-400 text-white text-[10px] flex items-center justify-center font-medium"
                            >
                              {rowIdx + 1}{String.fromCharCode(65 + Math.floor(busSeatsPerRow / 2) + seatIdx)}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Success Message */}
          {savedLayout && (
            <div className={`mt-4 p-4 rounded-lg ${savedLayout.is_published ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
              <div className="flex items-start gap-3">
                <Check className={`w-5 h-5 mt-0.5 ${savedLayout.is_published ? 'text-green-600' : 'text-amber-600'}`} />
                <div>
                  <p className={`font-medium ${savedLayout.is_published ? 'text-green-800' : 'text-amber-800'}`}>
                    {savedLayout.is_published ? 'Layout Published!' : 'Layout Saved as Draft'}
                  </p>
                  <p className={`text-sm mt-1 ${savedLayout.is_published ? 'text-green-700' : 'text-amber-700'}`}>
                    Layout ID: <code className="bg-white px-1 rounded">{savedLayout.layout_id || savedLayout.id}</code>
                  </p>
                  {savedLayout.is_published && savedLayout.widget_url && (
                    <p className="text-sm mt-2 text-green-700">
                      Widget URL: <a href={savedLayout.widget_url} target="_blank" rel="noopener noreferrer" className="underline">{savedLayout.widget_url}</a>
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className={`p-6 rounded-xl ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="animate-spin w-8 h-8 border-4 border-violet-600 border-t-transparent rounded-full mx-auto"></div>
            <p className="mt-3 text-sm">Processing...</p>
          </div>
        </div>
      )}
    </div>
  );
}
